import { useState, useMemo } from 'react'
import { useUpdateDocument } from '@/hooks/useFirestore'
import { useBusinesses, useUsers } from '@/hooks/useAdminData'
import { byCreatedAtDesc } from '@/lib/normalize'
import { Card, CardContent } from '@/components/ui/Card'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { useTranslation } from 'react-i18next'
import { useLocaleFormat } from '@/hooks/useLocaleFormat'
import { enumLabel } from '@/lib/enumLabel'
import type { Business } from '@/types'
import { Building2, Users, Search } from 'lucide-react'

export function BusinessesPage() {
  const { t } = useTranslation()
  const { formatDate } = useLocaleFormat()
  const [search, setSearch] = useState('')
  const [selectedBiz, setSelectedBiz] = useState<Business | null>(null)
  const [editBiz, setEditBiz] = useState<Business | null>(null)
  const [editName, setEditName] = useState('')
  const [editTier, setEditTier] = useState('')

  const { data: businesses, isLoading } = useBusinesses()
  const { data: users } = useUsers()
  const updateBusiness = useUpdateDocument('businesses', 'updatedAt')

  const userCountMap = useMemo(() => {
    const map: Record<string, number> = {}
    users?.forEach((u) => {
      if (u.businessId) map[u.businessId] = (map[u.businessId] || 0) + 1
    })
    return map
  }, [users])

  const filtered = useMemo(() => {
    if (!businesses) return []
    const sorted = [...businesses].sort(byCreatedAtDesc)
    if (!search) return sorted
    const lower = search.toLowerCase()
    return sorted.filter((b) => b.name?.toLowerCase().includes(lower))
  }, [businesses, search])

  const handleSaveEdit = async () => {
    if (!editBiz) return
    await updateBusiness.mutateAsync({
      id: editBiz.id,
      data: {
        name: editName,
        'subscription.tier': editTier,
      },
    })
    setEditBiz(null)
  }

  const statusBadgeVariant = (status?: string) => {
    switch (status) {
      case 'active': return 'success' as const
      case 'trialing': return 'warning' as const
      case 'past_due': return 'danger' as const
      case 'canceled': return 'danger' as const
      default: return 'default' as const
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-full flex-1 sm:max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder={t('businesses.searchPlaceholder')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full rounded-lg border border-gray-300 py-2 pl-9 pr-4 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
          />
        </div>
        <p className="text-sm text-gray-500">{t('businesses.count', { count: filtered.length })}</p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((biz) => (
          <Card key={biz.id} className="cursor-pointer hover:shadow-md transition-shadow">
            <CardContent className="space-y-3" onClick={() => setSelectedBiz(biz)}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="rounded-lg bg-purple-100 p-2">
                    <Building2 className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{biz.name}</h3>
                    <Badge variant={statusBadgeVariant(biz.subscription?.status)}>
                      {biz.subscription?.status
                        ? enumLabel(t, 'subscriptionStatus', biz.subscription.status)
                        : t('businesses.noSubscription')}
                    </Badge>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 text-xs text-gray-500">
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {t('businesses.userCount', { count: userCountMap[biz.id] ?? 0 })}
                </span>
                <span>{t('businesses.tier', { tier: enumLabel(t, 'tiers', biz.subscription?.tier || 'none') })}</span>
              </div>

              {biz.usage_stats && (
                <div className="grid grid-cols-3 gap-2 rounded bg-gray-50 p-2 text-center text-xs">
                  <div>
                    <p className="font-medium text-gray-900">{biz.usage_stats.care_recipients_count}</p>
                    <p className="text-gray-500">{t('businesses.recipients')}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{biz.usage_stats.caregivers_count}</p>
                    <p className="text-gray-500">{t('businesses.caregivers')}</p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{biz.usage_stats.documents_count}</p>
                    <p className="text-gray-500">{t('businesses.docs')}</p>
                  </div>
                </div>
              )}

              <p className="text-xs text-gray-400">
                {t('businesses.created', {
                  date: biz.createdAt ? formatDate(biz.createdAt.toDate()) : t('common.notAvailable'),
                })}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Modal
        open={!!selectedBiz}
        onClose={() => setSelectedBiz(null)}
        title={t('businesses.detailsTitle')}
        className="max-w-lg"
      >
        {selectedBiz && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 gap-4 rounded-lg bg-gray-50 p-4 sm:grid-cols-2">
              <div>
                <p className="text-xs text-gray-500">{t('businesses.name')}</p>
                <p className="text-sm font-medium">{selectedBiz.name}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('businesses.subscriptionTier')}</p>
                <p className="text-sm font-medium">{enumLabel(t, 'tiers', selectedBiz.subscription?.tier || 'none')}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('businesses.status')}</p>
                <Badge variant={statusBadgeVariant(selectedBiz.subscription?.status)}>
                  {enumLabel(t, 'subscriptionStatus', selectedBiz.subscription?.status ?? 'none')}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('businesses.billing')}</p>
                <p className="text-sm font-medium">
                  {selectedBiz.subscription?.billing_interval
                    ? enumLabel(t, 'billingInterval', selectedBiz.subscription.billing_interval)
                    : t('common.notAvailable')}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('businesses.users')}</p>
                <p className="text-sm font-medium">{userCountMap[selectedBiz.id] ?? 0}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('businesses.storage')}</p>
                <p className="text-sm font-medium">{t('businesses.storageMb', { value: selectedBiz.usage_stats?.storage_used_mb ?? 0 })}</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setEditBiz(selectedBiz)
                  setEditName(selectedBiz.name)
                  setEditTier(selectedBiz.subscription?.tier ?? '')
                  setSelectedBiz(null)
                }}
              >
                {t('businesses.edit')}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        open={!!editBiz}
        onClose={() => setEditBiz(null)}
        title={t('businesses.edit')}
      >
        {editBiz && (
          <div className="space-y-4">
            <Input
              id="bizName"
              label={t('businesses.businessName')}
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">{t('businesses.subscriptionTier')}</label>
              <select
                value={editTier}
                onChange={(e) => setEditTier(e.target.value)}
                className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
              >
                <option value="">{t('tiers.none')}</option>
                <option value="free">{t('tiers.free')}</option>
                <option value="family">{t('tiers.family')}</option>
                <option value="care_pro">{t('tiers.care_pro')}</option>
                <option value="enterprise">{t('tiers.enterprise')}</option>
              </select>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="secondary" onClick={() => setEditBiz(null)}>{t('common.cancel')}</Button>
              <Button onClick={handleSaveEdit} disabled={updateBusiness.isPending}>
                {updateBusiness.isPending ? t('common.saving') : t('common.saveChanges')}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}
